import type { Table as TanstackTable } from "@tanstack/react-table";
import { cn } from "@/lib/utils";

/** Zero-based page indexes to show, centered on `current`, at most `size` long. */
export function pageWindow(current: number, count: number, size = 7): number[] {
  if (count <= 0) return [];
  if (count <= size) return Array.from({ length: count }, (_, i) => i);
  const half = Math.floor(size / 2);
  let start = Math.max(0, current - half);
  const end = Math.min(count - 1, start + size - 1);
  start = Math.max(0, end - size + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export function DataTablePagination<TData>({ table, noun }: { table: TanstackTable<TData>; noun: string }) {
  const { pageIndex, pageSize } = table.getState().pagination;
  const total = table.getFilteredRowModel().rows.length;
  const pageCount = table.getPageCount();
  const start = total === 0 ? 0 : pageIndex * pageSize + 1;
  const end = Math.min(total, (pageIndex + 1) * pageSize);

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <span>{total === 0 ? `No ${noun}` : `Showing ${start}–${end} of ${total} ${noun}`}</span>
      {pageCount > 1 && (
        <nav aria-label="Pagination" className="inline-flex items-center gap-0.5">
          {pageWindow(pageIndex, pageCount).map((i) => (
            <button
              key={i}
              type="button"
              onClick={() => table.setPageIndex(i)}
              aria-current={i === pageIndex ? "page" : undefined}
              className={cn(
                "h-[22px] min-w-[22px] px-1.5 text-[12px] text-muted-foreground hover:text-foreground",
                i === pageIndex && "font-semibold text-brand underline decoration-star decoration-2 underline-offset-[3px]"
              )}
            >
              {i + 1}
            </button>
          ))}
          <button
            type="button"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            aria-label="Next page"
            className="h-[22px] px-1.5 text-[14px] text-muted-foreground hover:text-foreground disabled:opacity-40"
          >
            ›
          </button>
        </nav>
      )}
    </div>
  );
}
